import { useTranslation } from 'react-i18next';
import EmptyState from 'components/EmptyState';
import { cn } from 'lib/utils';
import type { ImportExclusion, TechnicianImportValidateResponse } from 'api/technicianImportContract';
import { exclusionReasonLabel } from 'pages/AdminTechnician/validation';
import { exclusionCount } from '../importLabels';

/** CA10 — two groups: to ban (a new row included, created then banned, I23) and to reinstate. */
const ImportExclusionsPanel = ({ preview }: { preview: TechnicianImportValidateResponse }) => {
  const { t } = useTranslation();
  const { counts, exclusions } = preview;
  if (exclusionCount(preview) === 0) {
    return <EmptyState title={t('No ban in this file')} hint={t('No row fills in “Ban reason”.')} />;
  }
  const groups = [
    ['toExclude', t('Will be banned ({{count}})', { count: counts.toExclude }), 'text-rose-700 dark:text-rose-400'],
    ['toReinstate', t('Will be reinstated ({{count}})', { count: counts.toReinstate }), 'text-emerald-700 dark:text-emerald-400'],
  ] as const;

  return (
    <div className="space-y-2.5">
      {groups
        .filter(([group]) => exclusions[group].length > 0)
        .map(([group, title, tone]) => (
          <div key={group} className="overflow-hidden rounded-xl border">
            <div className={cn('border-b bg-muted/50 px-3.5 py-2 text-[12.5px] font-semibold', tone)}>{title}</div>
            {exclusions[group].map((entry: ImportExclusion) => (
              <div key={`${group}-${entry.row}`} className="grid grid-cols-[180px_90px_1fr] items-start gap-3 px-3.5 py-2.5 text-[13px]">
                <span className="font-medium">{entry.name}</span>
                <span className="text-muted-foreground">{t('row {{row}}', { row: entry.row })}</span>
                <span className="flex flex-col gap-0.5">
                  {group === 'toExclude' && entry.reason && <span>{exclusionReasonLabel(t, entry.reason)}</span>}
                  {group === 'toExclude' && entry.userId === null && <span>{t('New technician: created, then banned')}</span>}
                  {group === 'toExclude' && (
                    <span className="text-xs text-muted-foreground">
                      {t('Will no longer be offered for new interventions. Their ongoing missions remain.')}
                    </span>
                  )}
                  {group === 'toReinstate' && <span>{t('Reason removed in the file')}</span>}
                </span>
              </div>
            ))}
          </div>
        ))}
    </div>
  );
};

export default ImportExclusionsPanel;
